import { logger } from "@/core/logging";

import type { ConsentStatus } from "@/core/consent/domain";
import { hasConsent, isResolved } from "@/core/consent/domain";
import type {
  InstrumentationEvent,
  InstrumentationEventMap,
  InstrumentationSubscriber,
  InstrumentationSubscriberState,
  PublishInstrumentationEvent,
} from "@/core/instrumentation/instrumentation.types";

export type InstrumentationBus<TEvents extends InstrumentationEventMap = InstrumentationEventMap> =
  {
    /** Activate what the starting status already permits. Publishes nothing. */
    readonly start: (status: ConsentStatus) => void;
    /**
     * Apply a new consent status: (de)activate subscribers first, then deliver
     * `consent.changed` to the now-active set — so a subscriber activated BY
     * this change still learns the status that activated it, and one revoked by
     * it learns nothing after its teardown.
     */
    readonly setConsentStatus: (status: ConsentStatus) => void;
    /** Implements the vocabulary's publish signature — see its doc for why `consent.changed` is excluded. */
    readonly publish: PublishInstrumentationEvent<TEvents>;
    /** Read-only diagnostics: every subscriber and whether it is active. */
    readonly getSubscriberStates: () => readonly InstrumentationSubscriberState[];
    /** Deactivate everything. */
    readonly stop: () => void;
  };

type CreateInstrumentationBusOptions<TEvents extends InstrumentationEventMap> = {
  readonly subscribers: readonly InstrumentationSubscriber<TEvents>[];
  /** Topics held while the visitor has not answered, and delivered on the answer. */
  readonly holdUntilAnswered?: readonly Exclude<keyof TEvents, "consent.changed">[];
};

/** Bounds what a visitor who never answers keeps in memory for the page's lifetime. */
export const MAX_HELD_EVENTS = 100;

/**
 * Activation state is keyed on `name`, so two subscribers sharing one would
 * silently share a lifecycle: the second's activation would be skipped as
 * "already active" and the first's teardown would run for both. A programming
 * error in the composition array, so it fails at construction — loudly, and
 * before any vendor has been touched.
 */
const assertUniqueNames = (subscribers: readonly { readonly name: string }[]): void => {
  const seen = new Set<string>();
  for (const { name } of subscribers) {
    if (seen.has(name)) {
      throw new Error(`Instrumentation subscriber names must be unique; "${name}" is duplicated`);
    }
    seen.add(name);
  }
};

/**
 * A small typed publish/subscribe whose one job beyond delivery is owning the
 * consent-gated vendor lifecycle described on `InstrumentationSubscriber`.
 * Delivery is to ACTIVE subscribers only; the one thing delivered late is an
 * event on a held topic from before the visitor answered — see the subscriber
 * contract. Subscriber callbacks are isolated: one sink throwing is
 * logged and must never take down activation, delivery, or teardown of the
 * others.
 */
export const createInstrumentationBus = <
  TEvents extends InstrumentationEventMap = InstrumentationEventMap,
>({
  subscribers,
  holdUntilAnswered = [],
}: CreateInstrumentationBusOptions<TEvents>): InstrumentationBus<TEvents> => {
  assertUniqueNames(subscribers);
  const heldTopics = new Set<keyof TEvents>(holdUntilAnswered);
  const heldEvents: {
    readonly event: InstrumentationEvent<TEvents>;
    readonly deliveredTo: ReadonlySet<string>;
    readonly capturedAt: number;
  }[] = [];

  // Presence in this map IS the definition of "active" — activation state is
  // tracked here, on the bus's side, so activate/deactivate stay idempotent
  // even for subscribers whose own implementations are not.
  const activeTeardowns = new Map<string, (() => void) | undefined>();
  let currentStatus: ConsentStatus | null = null;

  const guard = (subscriber: string, phase: string, run: () => void): void => {
    try {
      run();
    } catch (error) {
      logger.error("instrumentation_subscriber_failed", { subscriber, phase }, error as Error);
    }
  };

  const deliver = (
    subscriber: InstrumentationSubscriber<TEvents>,
    event: InstrumentationEvent<TEvents>,
  ): void => {
    if (subscriber.handle) {
      guard(subscriber.name, "handle", () => subscriber.handle?.(event));
    }
  };

  const activate = (subscriber: InstrumentationSubscriber<TEvents>): void => {
    if (activeTeardowns.has(subscriber.name)) {
      return;
    }

    // Marked active BEFORE the callback runs (and even if it throws): a
    // partially-started vendor re-activated on the next status change is the
    // double-init this map exists to prevent, so a failed activation stays
    // "active but broken" and loud in the logs rather than silently retried.
    activeTeardowns.set(subscriber.name, undefined);
    guard(subscriber.name, "activate", () => {
      const teardown = subscriber.activate();
      if (typeof teardown === "function") {
        activeTeardowns.set(subscriber.name, teardown);
      }
    });
  };

  const deactivate = (subscriber: InstrumentationSubscriber<TEvents>): void => {
    if (!activeTeardowns.has(subscriber.name)) {
      return;
    }

    const teardown = activeTeardowns.get(subscriber.name);
    activeTeardowns.delete(subscriber.name);
    if (teardown) {
      guard(subscriber.name, "teardown", teardown);
    }
    if (subscriber.deactivate) {
      guard(subscriber.name, "deactivate", () => subscriber.deactivate?.());
    }
  };

  const syncActivation = (status: ConsentStatus): void => {
    for (const subscriber of subscribers) {
      if (hasConsent(status, subscriber.requiredScope)) {
        activate(subscriber);
      } else {
        deactivate(subscriber);
      }
    }
  };

  const deliverToActive = (event: InstrumentationEvent<TEvents>): ReadonlySet<string> => {
    const deliveredTo = new Set<string>();
    for (const subscriber of subscribers) {
      if (activeTeardowns.has(subscriber.name)) {
        deliver(subscriber, event);
        deliveredTo.add(subscriber.name);
      }
    }
    return deliveredTo;
  };

  // On the answer: each held event goes to the subscribers that answer activated, never again to
  // one that already had it live. A decline activates nobody new, so it is simply let go.
  const releaseHeldEvents = (): void => {
    for (const { event, deliveredTo, capturedAt } of heldEvents.splice(0)) {
      for (const subscriber of subscribers) {
        if (activeTeardowns.has(subscriber.name) && !deliveredTo.has(subscriber.name)) {
          deliver(subscriber, { ...event, capturedAt });
        }
      }
    }
  };

  return {
    start(status) {
      currentStatus = status;
      syncActivation(status);
    },

    setConsentStatus(status) {
      // Referential equality means "nothing happened" — the reducer returns
      // the same reference for every no-op signal precisely so this can skip.
      if (status === currentStatus) {
        return;
      }

      currentStatus = status;
      syncActivation(status);
      deliverToActive({
        topic: "consent.changed",
        payload: status,
      } as InstrumentationEvent<TEvents>);
      if (isResolved(status)) {
        releaseHeldEvents();
      }
    },

    publish(topic, payload) {
      const event = { topic, payload } as InstrumentationEvent<TEvents>;
      const deliveredTo = deliverToActive(event);
      const isAnswered = currentStatus !== null && isResolved(currentStatus);
      if (!isAnswered && heldTopics.has(topic) && heldEvents.length < MAX_HELD_EVENTS) {
        heldEvents.push({ event, deliveredTo, capturedAt: Date.now() });
      }
    },

    getSubscriberStates() {
      return subscribers.map((subscriber) => ({
        name: subscriber.name,
        requiredScope: subscriber.requiredScope,
        active: activeTeardowns.has(subscriber.name),
      }));
    },

    stop() {
      for (const subscriber of subscribers) {
        deactivate(subscriber);
      }
      heldEvents.length = 0;
      currentStatus = null;
    },
  };
};
